import { Injectable, computed, inject, signal } from '@angular/core';
import { CameraSource } from '@capacitor/camera';
import { FileCode } from '@core/enums/file-code';
import { FileHelper } from '@core/helpers/file.helper';
import { AlarmConfig } from '@core/interfaces/alarm-config';
import { FileInfo, getTaskFilePath } from '@core/interfaces/file-info';
import { TaskFiles } from '@core/models/task-files.model';
import { TemplateOfflineImage } from '@core/interfaces/offline/template-offline-image';
import { CameraPicture } from '@core/interfaces/requests/camera-picture';
import { VisualEvent } from '@core/interfaces/visual-event';
import { DatabaseService } from '@core/services/database.service';
import { CameraService } from 'src/app/native/services/camera.service';
import { FilesService as MobileFilesService } from '@mobile/services/files.service';
import { TaskFilesService as MobileTaskFilesService } from '@mobile/services/task-files.service';
import { FilesService as WebFilesService } from '@web/services/files.service';
import { TaskFilesService as WebTaskFilesService } from '@web/services/task-files.service';
import { RedlinesFetcherService } from '@web/services/redlines-fetcher.service';
import { RedlinesService } from 'src/app/mobile/services/redlines.service';
import { IsometricAlarmService } from '@shared/services/isometric-alarm.service';
import { NativeFileService } from '@shared/services/native-file.service';
import { ReportDataFetcherService } from '@shared/services/report-data-fetcher.service';
import { VisualEventsFetcherService } from '@shared/services/visual-events-fetcher.service';
import { VisualEventsService } from 'src/app/mobile/services/visual-events.service';
import { InspectionReportDataItems } from '@orm-entities/inspection-report-data-items';
import { ReportTemplateImages } from '@orm-entities/report-template-images';
import { ConfigurationService } from '@osc-utils/services/configuration.service';
import { WhiteboardUpdate } from '@osc-utils/models/whiteboard-update';
import { Observable, firstValueFrom, from, map, of, switchMap, tap } from 'rxjs';

@Injectable()
export class ReportService {
  readonly #configurationService = inject(ConfigurationService);
  readonly #reportDataFetcher = inject(ReportDataFetcherService);
  readonly #databaseService = inject(DatabaseService);
  readonly #visualEventsFetcher = inject(VisualEventsFetcherService);
  readonly #visualEventsService = inject(VisualEventsService);
  readonly #isometricAlarmService = inject(IsometricAlarmService);
  readonly #redlinesService = inject(RedlinesService);
  readonly #redlinesFetcherService = inject(RedlinesFetcherService);
  readonly #webTaskFilesService = inject(WebTaskFilesService);
  readonly #webFilesService = inject(WebFilesService);
  readonly #mobileTaskFilesService = inject(MobileTaskFilesService);
  readonly #mobileFilesService = inject(MobileFilesService);
  readonly #nativeFileService = inject(NativeFileService);
  readonly #cameraService = inject(CameraService);

  #taskFiles = signal<TaskFiles>(new TaskFiles([]));
  #taskImages = computed(() => this.#taskFiles().imagesAsc);

  public readonly inspectionReportFile = computed(() => this.#taskFiles().inspectionReport);
  public readonly taskImages = this.#taskImages;

  public isMobile(): boolean {
    return this.#configurationService.isMobileApp();
  }

  public getData(projectId: string, jobId: string, taskId: string): Observable<Record<string, string>> {
    return this.isMobile() ? this.#loadData(taskId) : this.#reportDataFetcher.fetchData(projectId, jobId, taskId);
  }

  public getTemplateImages(taskId: string): Observable<TemplateOfflineImage[]> {
    return this.isMobile() ? this.#loadTemplateImages(taskId) : of([]);
  }

  public getVisualEvents(taskId: string): Observable<VisualEvent[]> {
    return this.isMobile() ? this.#visualEventsService.loadVisualEvents(taskId) : this.#visualEventsFetcher.fetchVisualEvents(taskId);
  }

  public getRedlines(fileId: string, taskId: string): Observable<WhiteboardUpdate[]> {
    return this.isMobile() ? this.#redlinesService.load(fileId) : this.#redlinesFetcherService.fetch(fileId, taskId);
  }

  public getInteractiveIsometricAlarmConfiguration(cmlJsonFileId: string): Observable<AlarmConfig> {
    return this.isMobile()
      ? this.#isometricAlarmService.loadInteractiveIsometricAlarmConfiguration(cmlJsonFileId)
      : this.#isometricAlarmService.fetchInteractiveIsometricAlarmConfiguration(cmlJsonFileId);
  }

  public getTaskFiles(projectId: string, jobId: string, taskId: string): Observable<TaskFiles> {
    return this.isMobile()
      ? this.#mobileTaskFilesService.loadTaskFiles(projectId, jobId, taskId)
      : this.#webTaskFilesService.fetchTaskFiles(projectId, jobId, taskId);
  }

  public loadTaskFiles(projectId: string, jobId: string, taskId: string) {
    return firstValueFrom(this.getTaskFiles(projectId, jobId, taskId).pipe(tap(taskFiles => this.#taskFiles.set(taskFiles))));
  }

  public getTemplate(file: FileInfo): Observable<object> {
    return this.isMobile() ? this.#mobileFilesService.loadFileContent(file) : this.#webFilesService.fetchFileContent(file);
  }

  public updateTemplate(
    projectId: string,
    jobId: string,
    taskId: string,
    fileId: string,
    template: string,
    fileName: string,
    description: string,
    fileType: string,
  ): Observable<any> {
    return this.isMobile()
      ? this.#mobileTaskFilesService.updateTaskTemplate(projectId, jobId, taskId, fileId, template, fileName, description, fileType)
      : this.#webTaskFilesService.updateTaskTemplate(projectId, jobId, taskId, fileId, template, fileName, description, fileType);
  }

  public addHtmlReport(
    projectId: string,
    jobId: string,
    taskId: string,
    htmlReport: string,
    fileName: string,
    fileType: string,
    isRealTime = false,
  ): Observable<any> {
    return this.isMobile()
      ? this.#mobileTaskFilesService.addTaskHtmlReport(projectId, jobId, taskId, htmlReport, fileName, fileType)
      : this.#webTaskFilesService.uploadTaskHtmlReport(projectId, jobId, taskId, htmlReport, fileName, fileType, isRealTime);
  }

  public addImage(projectId: string, jobId: string, taskId: string, originalFileId?: string): Observable<FileInfo> {
    return this.#configurationService.isWebApp()
      ? this.#addImageWeb(projectId, jobId, taskId)
      : this.#addImageMobile(projectId, jobId, taskId, originalFileId);
  }

  #addImageWeb(projectId: string, jobId: string, taskId: string): Observable<FileInfo> {
    return from(this.#uploadImage()).pipe(
      switchMap(picture => {
        const fileName = FileHelper.generateFileName('ReportImage');
        return this.#webTaskFilesService.uploadTaskFile(
          projectId,
          jobId,
          taskId,
          picture.file,
          fileName,
          'Report image',
          FileCode.FIELD_INSPECTION_IMAGE,
        );
      }),
      switchMap(({ id }) => this.#webTaskFilesService.fetchTaskFile(projectId, jobId, taskId, id)),
    );
  }

  #addImageMobile(projectId: string, jobId: string, taskId: string, originalFileId?: string): Observable<FileInfo> {
    const fileName = FileHelper.generateFileName('ReportImage');
    return from(this.#pickPhoto()).pipe(
      switchMap(picture =>
        this.#mobileTaskFilesService.addTaskFile(
          projectId,
          jobId,
          taskId,
          fileName,
          'Report image',
          FileCode.FIELD_INSPECTION_IMAGE,
          new Date(),
          picture.fileBase64,
        ),
      ),
      switchMap(({ id }) => {
        if (originalFileId) {
          return this.#mobileTaskFilesService.deleteTaskFile(projectId, jobId, taskId, originalFileId).pipe(
            switchMap(() => this.#updateReportTemplateImage(taskId, id, fileName, originalFileId)),
            map(() => id),
          );
        }
        return of(id);
      }),
      switchMap(id => this.#mobileTaskFilesService.loadTaskFile(projectId, jobId, taskId, id)),
    );
  }

  #updateReportTemplateImage(taskId: string, newFileId: string, fileName: string, previousFileId: string): Observable<any> {
    return this.#databaseService.update(
      ReportTemplateImages,
      { offlineUriPath: getTaskFilePath(fileName, taskId), imageId: newFileId },
      { taskId, imageId: previousFileId },
    );
  }

  async #uploadImage(): Promise<CameraPicture> {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';

    await new Promise<void>(resolve => {
      fileInput.addEventListener('change', () => resolve(), { once: true });
      fileInput.click();
    });

    if (!fileInput.files || fileInput.files.length === 0) {
      throw new Error('No file selected');
    }

    const selectedFile = fileInput.files[0];
    const base64String = await FileHelper.blobToBase64Split(selectedFile);

    return {
      file: this.#nativeFileService.base64ToBlob(base64String, selectedFile.type),
      fileBase64: base64String,
    } as CameraPicture;
  }

  async #pickPhoto(): Promise<CameraPicture> {
    this.#cameraService.options.quality = 70;
    const picture = await this.#cameraService.pickPhoto({ from: CameraSource.Camera });
    this.#cameraService.options.quality = 50;
    return picture;
  }

  #loadData(taskId: string): Observable<Record<string, string>> {
    return this.#databaseService.select(InspectionReportDataItems, { taskId }).pipe(
      map(items => {
        if (items.length > 0) {
          return JSON.parse(items[0].inspectionReportData) as Record<string, string>;
        }
        return {
          'MeasurementAndVisualEventResponse.visualEvents': '[]',
          'MeasurementAndVisualEventResponse.points': '[]',
        };
      }),
    );
  }

  #loadTemplateImages(taskId: string): Observable<TemplateOfflineImage[]> {
    return this.#databaseService
      .select(ReportTemplateImages, { taskId })
      .pipe(map(images => images.map(element => ({ taskId, imageId: element.imageId, uri: element.offlineUriPath }))));
  }
}
